import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

/**
 * Renders text content with @mentions converted to clickable profile links.
 */
export const MentionText = ({ text }) => {
  if (!text) return null;
  
  const parts = text.split(/(@\w+)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('@')) {
          const username = part.slice(1);
          return (
            <Link
              key={`mention-${username}-${i}`}
              to={`/discover?search=${username}`}
              className="text-brand-accent font-medium hover:underline"
              data-testid={`mention-link-${username}`}
            >
              {part}
            </Link>
          );
        }
        return <span key={`text-${i}`}>{part}</span>;
      })}
    </>
  );
};

/**
 * A textarea with @mention autocomplete. 
 * Wraps a standard textarea and shows a dropdown when user types @.
 */
const MentionInput = ({ value, onChange, placeholder, rows = 3, className = '', 'data-testid': testId }) => {
  const { api } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [cursorPosition, setCursorPosition] = useState(0);
  const textareaRef = useRef(null);
  const dropdownRef = useRef(null);

  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.length < 1) {
      setSuggestions([]);
      return;
    }
    try {
      const res = await api.get(`/users/mention-search?q=${encodeURIComponent(query)}&limit=6`);
      setSuggestions(res.data);
    } catch {
      setSuggestions([]);
    }
  }, [api]);

  useEffect(() => {
    if (mentionQuery) {
      const timer = setTimeout(() => fetchSuggestions(mentionQuery), 200);
      return () => clearTimeout(timer);
    } else {
      setSuggestions([]);
    }
  }, [mentionQuery, fetchSuggestions]);

  const handleChange = (e) => {
    const val = e.target.value;
    const cursor = e.target.selectionStart;
    setCursorPosition(cursor);
    onChange(val);

    // Detect @mention pattern at cursor
    const textBefore = val.slice(0, cursor);
    const mentionMatch = textBefore.match(/@(\w*)$/);
    if (mentionMatch) {
      setMentionQuery(mentionMatch[1]);
      setShowDropdown(true);
      setSelectedIndex(0);
    } else {
      setShowDropdown(false);
      setMentionQuery('');
    }
  };

  const insertMention = (user) => {
    const textarea = textareaRef.current;
    const textBefore = value.slice(0, cursorPosition);
    const textAfter = value.slice(cursorPosition);
    const mentionStart = textBefore.lastIndexOf('@');
    const newText = textBefore.slice(0, mentionStart) + `@${user.username} ` + textAfter;
    onChange(newText);
    setShowDropdown(false);
    setMentionQuery('');
    setSuggestions([]);
    
    // Focus back and set cursor after mention
    setTimeout(() => {
      if (textarea) {
        const newPos = mentionStart + user.username.length + 2;
        textarea.focus();
        textarea.setSelectionRange(newPos, newPos);
      }
    }, 10);
  };

  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter' && showDropdown) {
      e.preventDefault();
      insertMention(suggestions[selectedIndex]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={rows}
        className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring ${className}`}
        data-testid={testId}
      />
      {showDropdown && suggestions.length > 0 && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 left-0 right-0 mt-1 bg-white border border-brand-primary/10 rounded-lg shadow-lg overflow-hidden max-h-48 overflow-y-auto"
          data-testid="mention-dropdown"
        >
          {suggestions.map((user, i) => (
            <button
              key={user.id}
              type="button"
              className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                i === selectedIndex ? 'bg-brand-accent/10' : 'hover:bg-surface-alt'
              }`}
              onClick={() => insertMention(user)}
              data-testid={`mention-option-${user.username}`}
            >
              <Avatar className="w-7 h-7">
                <AvatarImage src={user.avatar_url} />
                <AvatarFallback className="bg-brand-primary text-white text-xs">
                  {(user.display_name || user.username)?.[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <p className="text-sm font-medium text-text-primary truncate">
                  {user.display_name || user.username}
                </p>
                <p className="text-xs text-text-muted">@{user.username}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MentionInput;
